import { useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api, ApiError } from "../lib/api";
import { AnimatedAreaIcon } from "../components/map/AnimatedAreaIcon";
import { AreaOpeningCutscene } from "../components/animation/AreaOpeningCutscene";
import { Area3DWorldField } from "../components/3d/Area3DWorldField";
import { AREA_BY_ID, type AreaId, getAreaName } from "../content/map";
import type { AreaDetail, UnitSummary } from "../content/lesson";

// モチーフ絵文字
const MOTIF_EMOJIS: Record<string, string> = {
  // 空港
  "到着ゲート": "🛬", "案内板": "🛈", "連絡橋": "🌉", "スーツケース": "🧳",
  // 家
  "机": "🪑", "時計": "⏰", "かばん": "🎒", "郵便受け": "📮", "窓": "🪟", "布団": "🛌",
  // 学校
  "教室": "🏫", "黒板": "📋", "掲示板": "📌", "校門": "🚪", "チャイム": "🔔",
  // コンビニ
  "レジ": "🏪", "商品棚": "🛒", "おにぎり": "🍙", "飲み物": "🥤", "電子レンジ": "🎛️",
  // カフェ
  "メニュー": "📜", "カップ": "☕", "窓際席": "🪟", "テーブル": "🪑",
  // 駅
  "改札": "🎫", "路線図": "🗺️", "ホーム": "🚉", "券売機": "🪙",
  // 図書館
  "本棚": "📚", "貸出カウンター": "💁", "読書席": "✍️", "カード": "💳",
  // 公園
  "ベンチ": "🪵", "花壇": "🌸", "噴水": "⛲",
  // ともだちの家
  "玄関": "🚪", "リビング": "🛋️", "写真": "🖼️", "手土産": "🎁",
  // 病院
  "受付": "🏥", "診察室": "🩺", "問診票": "📝", "薬袋": "💊",
  // 役所
  "窓口": "🏢", "番号札": "🏷️", "書類": "📄",
  // アルバイト先
  "制服": "👔", "タイムカード": "⏱️", "休憩室": "🍵", "メモ": "📝"
};

export function AreaPage() {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const isJa = i18n.language === "ja";

  const mapArea = id ? AREA_BY_ID[id as AreaId] : undefined;
  const stageNum = mapArea ? mapArea.unlockOrder + 1 : 1;

  // オープニングシネマティック表示状態（初回入場時のみ表示、または見たい時だけ再表示）
  const [showOpening, setShowOpening] = useState<boolean>(() => {
    if (!id) return false;
    const seenKey = `nihongo_opening_seen_${id}`;
    const hasSeen = localStorage.getItem(seenKey);
    if (!hasSeen) {
      localStorage.setItem(seenKey, "true");
      return true;
    }
    return false;
  });

  // 学習手帳（全体のセクション・ユニット進捗を一覧できるHUDスライドシート）
  const [showNotebook, setShowNotebook] = useState<boolean>(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["area", id],
    queryFn: () => api<AreaDetail>(`/api/learner/areas/${id}`),
    enabled: !!id,
    // 未開放（403）は再試行せずにすぐマップへ戻す
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });

  if (isLoading) {
    return <div className="p-8 text-center text-muted text-xl">{t("common:loading")}</div>;
  }
  // 未開放のエリアを URL で直接開いた時はマップへ戻す（API は 403）
  if (isError && error instanceof ApiError && error.status === 403) {
    return <Navigate to="/" replace />;
  }
  if (isError || !data) {
    const isNotFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-8 text-center flex flex-col items-center justify-center min-h-[50vh]">
        <div className="text-4xl mb-3">🏝️</div>
        <p className="text-[#8f332c] font-bold text-lg mb-2">
          {isNotFound ? t("learner:area_not_found") : (error instanceof Error ? error.message : t("learner:area_not_found"))}
        </p>
        <div className="flex items-center gap-3 mt-4">
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 bg-amber-500 text-white font-bold rounded-xl shadow hover:bg-amber-600 transition active:scale-95 text-sm"
          >
            {isJa ? "もう一度試す" : "Retry"}
          </button>
          <Link to="/" className="text-[#273f7a] font-bold underline text-sm">
            {t("learner:back_to_map")}
          </Link>
        </div>
      </div>
    );
  }

  const { area, sections, units = [] } = data;

  if (!area.unlocked) {
    return <Navigate to="/" replace />;
  }

  // セクションID → ユニット一覧
  const unitsBySectionId = units.reduce(
    (acc, u) => {
      (acc[u.section_id] ??= []).push(u);
      return acc;
    },
    {} as Record<string, UnitSummary[]>,
  );

  const completedCount = units.filter((u) => u.completed).length;
  const totalCount = units.length;

  function handleOpenUnit(sectionId: string, unitIndex: number) {
    navigate(`/unit/${sectionId}/${unitIndex}`);
  }

  const displayName = mapArea ? getAreaName(mapArea, i18n.language) : (isJa ? area.name_ja : area.name_en);

  return (
    <div className="relative w-full h-screen bg-gradient-to-br from-[#fff7e6] via-[#f5fbf7] to-[#dcece8] overflow-hidden flex flex-col select-none">
      {/* エリアオープニングアニメーション */}
      {showOpening && mapArea && (
        <AreaOpeningCutscene
          areaId={mapArea.id}
          onFinish={() => setShowOpening(false)}
        />
      )}

      {/* エリアHUD ヘッダー（3D空間の上にオーバーレイ） */}
      <header className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between gap-2 p-3 sm:p-4 pointer-events-none">
        {/* 左側: 戻る ＋ エリア情報 */}
        <div className="flex items-center gap-2.5 pointer-events-auto">
          <button
            onClick={() => navigate("/")}
            aria-label={t("learner:back_to_map")}
            className="bg-white/95 backdrop-blur-md rounded-2xl shadow-md w-11 h-11 flex items-center justify-center text-[#8f332c] shrink-0 border border-[#d9b45f]/40 hover:bg-[#fff9ea] transition active:scale-95"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-md px-3.5 py-2 border border-[#d9b45f]/40 flex items-center gap-2.5">
            {mapArea && <AnimatedAreaIcon areaId={mapArea.id} size={32} />}
            <div className="min-w-0">
              <div className="text-[10px] sm:text-[11px] text-stone-500 font-bold leading-tight">
                ステージ {String(stageNum).padStart(2, "0")} • {area.level}
              </div>
              <h1 className="text-sm sm:text-base font-black text-[#273f7a] truncate">
                {displayName}
              </h1>
            </div>
            {/* 進捗カウンタ */}
            <div className="ml-1 pl-2.5 border-l border-amber-200 flex flex-col items-center">
              <span className="text-[10px] font-bold text-stone-400">CLEAR</span>
              <span className="text-xs font-black text-emerald-700">
                {completedCount}/{totalCount}
              </span>
            </div>
          </div>
        </div>

        {/* 右側: オープニング再再生 ＋ 学習手帳ボタン */}
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            onClick={() => setShowOpening(true)}
            title={isJa ? "オープニングをもう一度見る" : "Replay Opening"}
            className="hidden sm:flex items-center gap-1.5 bg-white/95 backdrop-blur-md rounded-2xl px-3 h-11 text-xs font-black text-[#8f332c] border border-[#d9b45f]/40 shadow-md hover:bg-[#fff9ea] transition active:scale-95"
          >
            <span>🎬</span>
            <span>{isJa ? "オープニング" : "Opening"}</span>
          </button>

          <button
            onClick={() => setShowNotebook(true)}
            title={t("learner:lesson_guide_book")}
            className="flex items-center gap-1.5 bg-gradient-to-r from-amber-100 to-orange-100 backdrop-blur-md rounded-2xl px-3.5 h-11 text-xs font-black text-[#8f332c] border border-amber-400/70 shadow-md hover:from-amber-200 hover:to-orange-200 transition active:scale-95"
          >
            <span className="text-base">📖</span>
            <span>{t("learner:lesson_guide_book")}</span>
          </button>
        </div>
      </header>

      {/* メイン: フルスクリーン 3D オープンワールド散策フィールド */}
      <main className="relative z-10 w-full h-full flex-1">
        {mapArea && (
          <Area3DWorldField
            areaData={data}
            areaId={mapArea.id}
            onOpenUnit={handleOpenUnit}
          />
        )}
      </main>

      {/* 学習手帳（HUD スライドドロワー） */}
      <AnimatePresence>
        {showNotebook && (
          <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-xs">
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 220 }}
              className="w-full max-w-md h-full bg-[#fffdf7] border-l-2 border-[#d9b45f] shadow-2xl flex flex-col"
            >
              {/* 手帳ヘッダー */}
              <div className="p-4 border-b border-amber-200/60 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📖</span>
                  <div>
                    <h2 className="text-base font-black text-[#8f332c]">
                      {t("learner:lesson_guide_book")}
                    </h2>
                    <p className="text-xs text-stone-500 font-bold">
                      {displayName} • {completedCount}/{totalCount} ユニット完了
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNotebook(false)}
                  className="w-9 h-9 rounded-full bg-white border border-stone-200 text-stone-600 hover:bg-stone-100 flex items-center justify-center font-bold text-sm transition"
                >
                  ✕
                </button>
              </div>

              {/* 手帳コンテンツ: セクション＆ユニット一覧 */}
              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
                {sections.map((s) => {
                  const sectionUnits = (unitsBySectionId[s.id] ?? []).sort(
                    (a, b) => a.unit_index - b.unit_index,
                  );
                  return (
                    <div
                      key={s.id}
                      className="bg-white rounded-2xl p-4 border border-[#d9b45f]/30 shadow-xs flex flex-col gap-2.5"
                    >
                      <div className="flex items-center justify-between pb-2 border-b border-amber-100">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${s.cleared
                                ? "bg-emerald-500 text-white"
                                : "bg-[#8f332c] text-white"
                              }`}
                          >
                            {s.cleared ? "✓" : s.index}
                          </span>
                          <span className="text-sm font-black text-[#273f7a]">
                            {isJa ? s.title_ja : s.title_en}
                          </span>
                        </div>
                        <span className="text-xs font-bold text-stone-500">
                          {s.unit_done}/{s.unit_total}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-1.5">
                        {sectionUnits.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setShowNotebook(false);
                              handleOpenUnit(s.id, u.unit_index);
                            }}
                            className={`flex items-center justify-between p-2.5 rounded-xl border text-left transition ${u.completed
                                ? "bg-emerald-50/80 border-emerald-300 text-emerald-950"
                                : "bg-stone-50 hover:bg-amber-50 border-stone-200 text-stone-800"
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-base select-none">
                                {u.completed ? "⭐" : "🎯"}
                              </span>
                              <span className="font-bold text-xs">
                                {isJa ? `ユニット ${u.unit_index}` : `Unit ${u.unit_index}`}
                              </span>
                            </div>
                            <span className="text-[10px] font-black text-amber-800 bg-amber-100/80 px-2 py-0.5 rounded-full">
                              {u.completed ? "CLEAR" : "挑戦する 🚀"}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* エリアのストーリー＆生活モチーフ */}
                {mapArea && (
                  <div className="bg-amber-50/70 rounded-2xl p-4 border border-amber-200/60 flex flex-col gap-2 mt-2">
                    <div className="text-xs font-black text-[#8f332c]">
                      🌸 {isJa ? "この街での生活" : "Life in this area"}
                    </div>
                    <p className="text-xs text-stone-700 leading-relaxed font-bold">
                      {isJa ? mapArea.storyCopyJa : mapArea.storyCopyEn}
                    </p>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {mapArea.visualMotifs.map((motif) => {
                        const emoji = MOTIF_EMOJIS[motif] || "✨";
                        return (
                          <span
                            key={motif}
                            className="inline-flex items-center gap-1 rounded-full bg-white border border-[#d9b45f]/30 text-[#8f332c] text-[11px] font-black px-2 py-0.5 shadow-2xs"
                          >
                            <span>{emoji}</span>
                            <span>{motif}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
